import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, TrendingUp, Sparkles, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n";
import { properties } from "../data/properties";
import { PropertyCard } from "../components/PropertyCard";
import { imgProxy } from "../lib/imgProxy";

export default function HomePage() {
  const { t } = useI18n();
  const featured = properties.filter((p) => p.favorite).slice(0, 3);

  return (
    <main>
      {/* Hero */}
      <section className="hero-bg h-[88vh] min-h-[620px] flex items-center" data-testid="hero-section">
        <div className="hero-img"
          style={{ backgroundImage: `url('${imgProxy("https://customer-assets.emergentagent.com/job_chamonix-locations/artifacts/p3abqdk4_monte-bianco-panorama.jpg", { w: 2000, q: 78 })}')` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 text-white w-full">
          <div className="max-w-3xl mx-auto text-center fade-in">
            <p className="text-[11px] sm:text-xs uppercase tracking-[.3em] opacity-90 mb-6 flex items-center justify-center gap-3">
              <span className="h-px w-8 bg-[hsl(var(--gold))]" /> {t.hero.tagline} <span className="h-px w-8 bg-[hsl(var(--gold))]" />
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
              {t.hero.title}<br />
              <span className="italic text-white">{t.hero.titleAccent}</span>
            </h1>
            <p className="text-base sm:text-lg opacity-90 leading-relaxed mb-10 max-w-xl mx-auto">{t.hero.subtitle}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/logements" data-testid="hero-cta-properties"
                className="btn-gold px-7 py-3.5 rounded-full text-sm font-semibold inline-flex items-center gap-2">
                {t.hero.ctaPrimary} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/louer-votre-bien" data-testid="hero-cta-rent"
                className="px-7 py-3.5 rounded-full text-sm font-semibold border border-white/50 text-white hover:bg-white hover:text-[hsl(var(--ink))] transition-all">
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: "4,86", l: t.hero.stats.rating, icon: Star },
            { v: "525+", l: t.hero.stats.reviews, icon: MessageCircle },
            { v: "11", l: t.hero.stats.properties, icon: Sparkles },
            { v: "7/7", l: t.hero.stats.service, icon: TrendingUp },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-4">
              <s.icon className="w-7 h-7 text-[hsl(var(--gold))]" />
              <div>
                <div className="font-serif text-3xl">{s.v}</div>
                <div className="text-[11px] uppercase tracking-wider opacity-70">{s.l}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Promise */}
      <section className="py-24 max-w-7xl mx-auto px-5 sm:px-8" data-testid="promise-section">
        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div>
            <div className="divider-gold mb-6" />
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-6">{t.home.promiseTitle}</h2>
            <p className="text-[hsl(var(--ink-soft))] leading-relaxed text-base">{t.home.promiseText}</p>
            <Link to="/services" data-testid="home-services-link" className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-[hsl(var(--gold-deep))] hover:gap-3 transition-all">
              {t.nav.services} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-6">
            {[{t: t.home.p1, d: t.home.p1d}, {t: t.home.p2, d: t.home.p2d}, {t: t.home.p3, d: t.home.p3d}].map((it, i) => (
              <div key={i} className="flex gap-5 p-6 bg-[hsl(var(--bg-warm))] rounded-xl border border-[hsl(var(--line))]">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[hsl(var(--gold))] text-white flex items-center justify-center font-serif">{i+1}</div>
                <div>
                  <h3 className="font-serif text-xl mb-1.5">{it.t}</h3>
                  <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{it.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="py-24 bg-[hsl(var(--bg-warm))]" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="divider-gold mb-5" />
              <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t.home.featuredTitle}</h2>
              <p className="text-[hsl(var(--ink-soft))] mt-3">{t.home.featuredSub}</p>
            </div>
            <Link to="/logements" className="btn-outline-ink px-6 py-3 rounded-full text-sm font-semibold" data-testid="home-see-all">
              {t.home.seeAll} →
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-5 sm:px-8">
        <div className="max-w-5xl mx-auto relative overflow-hidden rounded-2xl bg-[hsl(var(--ink))] text-[hsl(var(--bg))] p-12 sm:p-16">
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight mb-4">{t.home.ctaTitle}</h2>
            <p className="opacity-80 mb-8">{t.home.ctaText}</p>
            <Link to="/louer-votre-bien" data-testid="home-cta-rent"
              className="btn-gold inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold">
              {t.home.ctaBtn} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[hsl(var(--gold))]/10 blur-3xl" />
        </div>
      </section>
    </main>
  );
}
